import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaFingerprint, FaInfinity, FaMicrochip, FaTimes } from 'react-icons/fa';
import './HomeLogoStoryModal.css';

const storySections = (t) => [
  {
    icon: <FaInfinity />,
    title: t('customerUi.logoStory.sections.growth.title'),
    text: (
      <>
        {t('customerUi.logoStory.sections.growth.text')}
        <strong>{t('customerUi.logoStory.sections.growth.emphasis')}</strong>
      </>
    ),
  },
  {
    icon: <FaFingerprint />,
    title: t('customerUi.logoStory.sections.roots.title'),
    text: t('customerUi.logoStory.sections.roots.text'),
  },
  {
    icon: <FaMicrochip />,
    title: t('customerUi.logoStory.sections.technology.title'),
    text: t('customerUi.logoStory.sections.technology.text'),
  },
];

const HomeLogoStoryModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="home-logo-story"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="home-logo-story__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-logo-story-title"
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="home-logo-story__close ui-close-button"
          onClick={onClose}
          aria-label={t('customerUi.logoStory.close')}
        >
          <FaTimes aria-hidden="true" />
        </button>

        <div className="home-logo-story__visual">
          <span className="home-logo-story__label">HARYAD / BRAND STORY</span>
          <div className="home-logo-story__logo">
            <img src="/images/home/background/haryad-logo.webp" alt={t('customerUi.logoStory.logoAlt')} />
          </div>
          <blockquote>{t('customerUi.logoStory.quote')}</blockquote>
          <p>{t('customerUi.logoStory.visualText')}</p>
        </div>

        <div className="home-logo-story__content">
          <header>
            <span>{t('customerUi.logoStory.eyebrow')}</span>
            <h2 id="home-logo-story-title">{t('customerUi.logoStory.title')}</h2>
            <p>{t('customerUi.logoStory.intro')}</p>
          </header>

          <div className="home-logo-story__sections">
            {storySections(t).map((section) => (
              <article key={section.title}>
                <span className="home-logo-story__icon" aria-hidden="true">
                  {section.icon}
                </span>
                <div>
                  <h3>{section.title}</h3>
                  <p>{section.text}</p>
                </div>
              </article>
            ))}
          </div>

          <footer>
            <span>{t('customerUi.logoStory.footerLabel')}</span>
            <strong>{t('customerUi.logoStory.footerText')}</strong>
          </footer>
        </div>
      </section>
    </div>,
    document.body
  );
};

export default HomeLogoStoryModal;
